import { useNavigate } from 'react-router-dom';
import { formatToShortDateOrNull } from '~/util/date';
import { createQueryDataTable, type PropsOfQueryDataTable, type RowOfQueryDataTable } from '~/shared/data-tables/query/QueryDataTable';
import { PatientAvatar } from '~/features/intake/patients/PatientAvatar';
import { Badge } from '~/shared/components/Badge';
import { PlanOfCareReviewStatusEnum, PriorityEnum } from '@generated';
import { getStatusColor, type StatusIntents } from '~/theme/variants';
import { AlertCircle, CircleCheck, Clock, Eye, Flag, RefreshCcw, type LucideIcon } from 'lucide-react';
import { TableActions } from '~/shared/data-tables/TableActions';

const PlanOfCareReviewDataTable = createQueryDataTable('plan-of-care-reviews');
type RowType = RowOfQueryDataTable<typeof PlanOfCareReviewDataTable>;
type TableProps = PropsOfQueryDataTable<typeof PlanOfCareReviewDataTable>;

function ActionsCell({ row }: { row: RowType }) {
	const navigate = useNavigate();

	const view = () => {
		navigate(`${row.id}`);
	};

	return (
		<TableActions>
			<TableActions.ViewButton useSuspense onClick={view} />
		</TableActions>
	);
}

const statusVariants: { statusId: number; variant: StatusIntents; Icon: LucideIcon }[] = [
	{ statusId: PlanOfCareReviewStatusEnum.InProgress, variant: 'warning', Icon: Eye },
	{ statusId: PlanOfCareReviewStatusEnum.Approved, variant: 'success', Icon: CircleCheck },
	{ statusId: PlanOfCareReviewStatusEnum.Returned, variant: 'destructive', Icon: RefreshCcw }
];

export function StatusBadge(props: { statusId: number; statusLabel: string }) {
	const status = statusVariants.find(s => s.statusId === props.statusId)!;
	return (
		<Badge intent={status?.variant}>
			<status.Icon className="mr-1 size-3" />
			{props.statusLabel}
		</Badge>
	);
}

export function PriorityBadge(props: { priorityId: number; priorityLabel: string }) {
	return (
		<Badge intent={props.priorityId === PriorityEnum.High ? 'destructive' : props.priorityId === PriorityEnum.Medium ? 'warning' : 'neutral'}>
			<Flag className="mr-1 size-3" />
			{props.priorityLabel}
		</Badge>
	);
}

const today = new Date();

export function DeadlineText(props: { dueAt: Date }) {
	const daysUntilDue = Math.ceil((props.dueAt.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
	const statusColor = getStatusColor(
		daysUntilDue < 0 ? 'destructive' : daysUntilDue === 0 ? 'warning' : daysUntilDue === 1 ? 'warning' : 'neutral'
	);
	const icon = daysUntilDue < 0 ? <AlertCircle size={12} /> : daysUntilDue <= 3 ? <Clock size={12} /> : null;
	const text = daysUntilDue < 0 ? `${Math.abs(daysUntilDue)}d overdue` : `${daysUntilDue}d left`;
	return (
		<div className={`${statusColor} flex items-center gap-1 text-xs font-medium`}>
			{icon}
			{text}
		</div>
	);
}

const columnDefinitions = PlanOfCareReviewDataTable.columns([
	{
		header: 'Name',
		key: 'patientName',
		sortable: true,
		width: 'w-[250px]',
		renderCell: row => (
			<PatientAvatar
				{...row}
				firstName={row.patientFirstName}
				lastName={row.patientLastName}
				middleInitial={row.patientMiddleInitial}
				visitAddress={row.visitAddressAddressOne}
			/>
		)
	},
	{
		header: 'SOC Date',
		key: 'startOfCareDate',
		sortable: true,
		renderCell: row => formatToShortDateOrNull(row.startOfCareDate, '-')
	},
	{
		header: 'Case Manager',
		key: 'caseManagerName',
		sortable: true,
		renderCell: row => row.caseManagerName ?? '-'
	},
	{
		header: 'Physician',
		key: 'primaryPatientPhysicianName',
		sortable: true,
		renderCell: row => row.primaryPatientPhysicianName
	},
	{
		header: 'Payor',
		key: 'primaryPatientPayorInsuranceProviderName',
		sortable: true,
		renderCell: row => row.primaryPatientPayorInsuranceProviderName ?? '-'
	},
	{
		header: 'Status',
		key: 'planOfCareReviewStatusLabel',
		sortable: true,
		renderCell: row => <StatusBadge {...row} />
	},
	{
		header: 'Priority',
		key: 'priorityLabel',
		sortable: true,
		renderCell: row => <PriorityBadge {...row} />
	},
	{
		header: 'Deadline',
		key: 'dueAt',
		sortable: true,
		renderCell: row => <DeadlineText dueAt={row.dueAt} />
	},
	{
		header: 'Actions',
		key: 'actions',
		renderCell: row => <ActionsCell row={row} />
	}
]);

export function PlanOfCareReviewTable(props: TableProps) {
	return <PlanOfCareReviewDataTable.Table columns={columnDefinitions} emptyMessage="No certification reviews found." {...props} />;
}
